import { useEffect, useState, useRef } from 'react';
import { useCustomer } from '@backpackjs/storefront';
import Link from 'next/link';
import AccountLayout from '../../layouts/AccountLayout';
import { useCustomerDetails } from '@hooks';
import { Image } from '@/snippets/components/Image';

export function MyRewards({ cms }) {
  const customer = useCustomer();
  const [loaded, setLoaded] = useState(false);

  const [points, setPoints] = useState('');
  const { customerDetails } = useCustomerDetails({
    email: customer?.email,
  });

  useEffect(() => {
    setPoints(customerDetails?.points_balance || 0);
  }, [customerDetails?.points_balance]);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (window?.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      window.yotpoWidgetsContainer.initWidgets(); // Initialize the Yotpo widget 
    }
  }, [loaded]);

  return (
    <div className="px-0 pt-3 pb-10" id="rewards">
      <div className="border border-b-0 border-grey-1 pt-6 md:pt-16 pb-0 md:pb-16 bg-light-blue relative z-0 max-md:!aspect-auto"
        style={{ aspectRatio: cms.myRewardImage?.aspectRatio || 0.65 }}
      >
        <div className="w-full flex flex-col md:flex-row">
          <div className="md-:w-1/2 px-4 md:pr-4 md:pl-12 text-grey-1 text-center md:text-left pb-6 md:pb-0 relative z-[1]">
            <h1 className="h3 text-h2-m md:text-[42px] md:leading-none mb-3 md:mb-4">My Briogeo Rewards</h1>
            <p className="text-xsmall md:text-sm mb-6">Once you're done shopping, login or sign up to redeem coupons you qualify for at checkout.</p>
            <div className="border border-quinary-2 inline-block text-center text-grey-1 w-[214px] max-w-full">
              <div className="px-4 py-2 bg-quinary-2 text-regular font-medium">Points Balance</div>
              <div className="px-4 py-2 text-h5 font-bold">{points}</div>
            </div>
            {loaded && points === 0 && (
              <div className="mt-4">
                <a href="/account/referral" className={`text-center text-sm font-gothamBold border-b-2 border-grey-1 no-underline`} target="_blank">
                  refer a friend
                </a>
                <span className="text-xsmall ml-3 align-middle font-gothamBold text-primary-2">
                  Earn 500 pts!
                </span>
              </div>
            )}
          </div>
          <div className="md:absolute z-0 top-0 pointer-events-none !h-full w-full">
            {/* mobile image */}
            {cms?.myRewardImageM ? (
              <div
                className="relative md:hidden h-full"
              >
                <Image
                  src={cms.myRewardImageM.src}
                  className="relative inset-0 !h-full !w-full object-right-bottom"
                  loading={'eager'}
                  fill={false}
                  width={cms.myRewardImageM?.width}
                  height={cms.myRewardImageM?.height}
                  sizes="100vw"
                  alt={cms.myRewardImageM.alt}
                  priority={true}
                  fetchpriority={'high'}
                />
              </div>
            ) : null}

            {/* desktop image */}
            {cms?.myRewardImage ? (
              <div
                className={`relative  h-full ${cms?.myRewardImageM ? 'max-md:hidden' : ''}`}
              >
                <Image
                  src={cms.myRewardImage.src}
                  className="absolute inset-0 !h-full !w-full object-right-bottom"
                  loading={'eager'}
                  fill
                  sizes="100vw"
                  alt={cms.myRewardImage.alt}
                  priority={true}
                  fetchpriority={'high'}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="border border-grey-1 px-3 py-7 md:px-10 md:py-8">
        {customer && (
          <div
            id="swell-customer-identification"
            data-authenticated="true"
            data-email={customer?.email}
            data-id={customer?.id?.replace('gid://shopify/Customer/', '')}
            data-tags='"[]"'
            style={{ display: 'none' }}
          />
        )}
        
        <div className="yotpo-widget-instance" data-yotpo-instance-id={cms?.yotpoWidgetId || '1106786'} />
        <div className="yotpo-widget-instance" data-yotpo-instance-id="23934" mode-rewards-history-only="true" />
        {loaded && points > 0 && (
          <div className="flex gap-x-11 gap-y-6 justify-center items-center mt-10 flex-wrap">
            <a href="#rewards-history"
              onClick={(e) => e.preventDefault()}
              className={`text-center text-sm font-gothamBold border-b-2 border-grey-1 no-underline yotpo-rewards-pop-up`}>
              rewards history
            </a>
            <a href="/pages/rewards" className={`text-center text-sm font-gothamBold border-b-2 border-grey-1 no-underline`} target="_blank">
              earn more points
            </a>
            <div className="w-full sm:w-auto text-center">
              <a href="/account/referral" className={`text-center text-sm font-gothamBold border-b-2 border-grey-1 no-underline`} target="_blank">
                refer a friend
              </a>
              <span className="text-xsmall ml-3 align-middle font-gothamBold text-primary-2">
                Earn 500 pts!
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


MyRewards.displayName = 'My Rewards';

MyRewards.Schema = {
  category: 'Account',
  label: MyRewards.displayName,
  key: 'account-my-rewards',
  fields: [
    {
      component: 'number',
      name: 'yotpoWidgetId',
      label: 'yotpo Widget Id',
      defaultValue: 892619,
    },
    {
      label: 'Image (Desktop)',
      name: 'myRewardImage',
      component: 'image',
      description: 'Recommended Image Size: 888 x 576',
    },
    {
      label: 'Image (Mobile)',
      name: 'myRewardImageM',
      component: 'image',
      description: 'Recommended Image Size: 686 x 390',
    },
    {
      component: 'number',
      name: 'mobileSpacing',
      label: 'Section Top Margin - Mobile',
      defaultValue: 60,
    },
    {
      component: 'number',
      name: 'desktopSpacing',
      label: 'Section Top Margin - Desktop',
      defaultValue: 80,
    },
    {
      component: 'number',
      name: 'mobileBottomSpacing',
      label: 'Section Bottom Margin - Mobile',
      defaultValue: 60,
    },
    {
      component: 'number',
      name: 'desktopBottomSpacing',
      label: 'Section Bottom Margin - Desktop',
      defaultValue: 80,
    },
  ]
}